import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useFetchSimilarVehicles } from 'Components/pages/VehicleDetailsRedesign/useFetchSimilarVehicles/useFetchSimilarVehicles';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { triggerRecommendedVehiclesLoaded, triggerRecommendedVehiclesLoadFailed } from 'Services/analytics/events';
import { ForSaleCategory } from 'Types/enquiryStates';
import type { VehicleListItem } from 'Types/vehicle';
import { useNormaliseMakeAndModel } from 'Utilities/hooks/useNormaliseMakeAndModel/useNormaliseMakeAndModel';

interface SimilarVehiclesContextType {
	isSimilarVehiclesLoading: boolean;
	isSimilarVehiclesModalOpen: boolean;
	isSimilarVehiclesSectionVisible: boolean;
	setIsSimilarVehiclesModalOpen: (isOpen: boolean) => void;
	similarVehicles: VehicleListItem[];
}

export const SimilarVehiclesContext = createContext<SimilarVehiclesContextType | undefined>(undefined);

export const SimilarVehiclesProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const [hasLoadedSimilarVehicles, setHasLoadedSimilarVehicles] = useState(false);
	const [isSimilarVehiclesModalOpen, setIsSimilarVehiclesModalOpen] = useState(false);
	const {
		similarVehicles: { id, make, modelId, stateSlug },
	} = useVehicleData();
	const isVehicleInSellingState = [ForSaleCategory.forSale, ForSaleCategory.saleActive].includes(
		stateSlug as ForSaleCategory,
	);
	const { normalisedMake, normalisedModel } = useNormaliseMakeAndModel({ make, modelId });
	const { data, isError, isLoading } = useFetchSimilarVehicles({
		enquiryId: id,
		isEnabled: isVehicleInSellingState,
		normalisedMake,
		normalisedModel,
		stateSlug,
	});
	const { similarVehicles = [] } = data || {};

	useEffect(() => {
		if (isError) {
			triggerRecommendedVehiclesLoadFailed();
			return;
		}
		if (similarVehicles && similarVehicles.length > 0 && !hasLoadedSimilarVehicles) {
			triggerRecommendedVehiclesLoaded({
				enquiryIds: similarVehicles.map((vehicle) => vehicle.id),
				version: data?.version,
			});
			setHasLoadedSimilarVehicles(true);
		}
	}, [similarVehicles, isError, data?.version, hasLoadedSimilarVehicles]);

	const isSimilarVehiclesSectionVisible =
		isVehicleInSellingState && (isLoading || (!isLoading && !isError && similarVehicles?.length > 0));

	const values = useMemo(() => {
		return {
			isSimilarVehiclesLoading: isLoading,
			isSimilarVehiclesModalOpen,
			isSimilarVehiclesSectionVisible,
			setIsSimilarVehiclesModalOpen,
			similarVehicles,
		};
	}, [
		isSimilarVehiclesModalOpen,
		isSimilarVehiclesSectionVisible,
		setIsSimilarVehiclesModalOpen,
		similarVehicles,
		isLoading,
	]);

	return <SimilarVehiclesContext.Provider value={values}>{children}</SimilarVehiclesContext.Provider>;
};

export const useSimilarVehiclesContext = (): SimilarVehiclesContextType => {
	const context = useContext(SimilarVehiclesContext);
	if (!context) {
		throw new Error('useSimilarVehiclesContext must be used within a SimilarVehiclesProvider');
	}
	return context;
};
